import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { useAdminDiagrams, AdminDiagram } from "@/hooks/useAdminDiagrams"
import { ArrowsRightLeftIcon, Squares2X2Icon, CalendarIcon, UserGroupIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

const PAGE_SIZE = 90

export function Diagrams() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const creator = searchParams.get("creator") ?? undefined
  const creatorName = searchParams.get("creatorName") ?? undefined
  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  // Search server-side (the endpoint already supports it) rather than filtering
  // only the current 90-row page — otherwise a match past page 1 reads as "no
  // results" until the admin manually pages to it.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // A new creator filter (or clearing it) starts back at page 0 — otherwise a
  // deep page from one user's diagrams could carry over as an out-of-range
  // offset for another.
  useEffect(() => {
    setPage(0)
  }, [creator])

  const { data, isLoading, error } = useAdminDiagrams({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch,
    creator,
  })

  const columns = useMemo<ColumnDef<AdminDiagram>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-primary">{row.original.name}</p>
            {row.original.description && (
              <p className="text-xs text-secondary truncate max-w-xs">
                {row.original.description}
              </p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "creator_name",
        header: "Creator",
        cell: ({ row }) => (
          <div>
            <p className="text-sm">{row.original.creator_name ?? "—"}</p>
            {row.original.creator_email && (
              <p className="text-xs text-secondary">{row.original.creator_email}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "team_name",
        header: "Team",
        cell: ({ row }) =>
          row.original.team_name ? (
            <span className="text-sm">{row.original.team_name}</span>
          ) : (
            <span className="text-xs text-secondary italic">Personal</span>
          ),
      },
      {
        id: "formations",
        header: "Formation",
        cell: ({ row }) => {
          const off = row.original.offensive_formation_id
          const def = row.original.defensive_formation_id
          const parts = [off, def].filter(Boolean)
          return parts.length > 0 ? (
            <span className="text-xs text-secondary font-mono">{parts.join(" · ")}</span>
          ) : (
            <span className="text-xs text-secondary italic">—</span>
          )
        },
      },
      {
        accessorKey: "play_type",
        header: "Type",
        cell: ({ row }) =>
          row.original.play_type ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary">
              {row.original.play_type}
            </span>
          ) : (
            <span className="text-xs text-secondary italic">—</span>
          ),
      },
      {
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ row }) => (
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm text-secondary">
              {new Date(row.original.updated_at).toLocaleDateString()}
            </span>
          </div>
        ),
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const diagrams = data?.diagrams ?? []

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Diagrams</h1>
            <p className="text-secondary">
              {isLoading
                ? "Loading…"
                : // "Showing N", not "N diagrams found": each page only carries this
                  // server page's rows, so a flat count would misread as a grand
                  // total once there's more than one page.
                  creator
                  ? `Showing ${diagrams.length} diagram${diagrams.length === 1 ? "" : "s"} by ${creatorName ?? "this user"}${page > 0 ? ` (page ${page + 1})` : ""}`
                  : `Showing ${diagrams.length} diagram${diagrams.length === 1 ? "" : "s"}${page > 0 ? ` (page ${page + 1})` : " across all users"}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {creator && (
              <Link
                to="/diagrams"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
                Clear filter
              </Link>
            )}
            <Button
              variant="outline"
              onClick={() =>
                navigate(
                  `/diagrams/transfer${creator ? `?creator=${encodeURIComponent(creator)}` : ""}`
                )
              }
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
              Transfer diagrams
            </Button>
            <Button variant="outline" onClick={() => navigate("/diagrams/team-ownership")}>
              <UserGroupIcon className="h-4 w-4" />
              Put playbooks on their team
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Input
            placeholder="Search diagrams by name or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="max-w-md"
          />
          {debouncedSearch && (
            <Button variant="outline" onClick={() => setSearchInput("")}>
              Clear
            </Button>
          )}
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : diagrams.length === 0 && page === 0 ? (
          <EmptyState
            icon={<Squares2X2Icon className="h-12 w-12" />}
            title={debouncedSearch ? "No matches" : "No diagrams found"}
            description={
              debouncedSearch
                ? `No diagrams match "${debouncedSearch}".`
                : creator
                  ? `${creatorName ?? "This user"} hasn't created any diagrams yet.`
                  : "No diagrams have been created yet."
            }
          />
        ) : (
          <>
            {diagrams.length === 0 ? (
              // Landed past the last page (e.g. the count is an exact multiple of
              // PAGE_SIZE) — keep Previous reachable instead of dead-ending here.
              <EmptyState
                icon={<Squares2X2Icon className="h-12 w-12" />}
                title="No more diagrams"
                description="You've reached the end of the list."
              />
            ) : (
              // The Previous/Next below already paginate the server-fetched page;
              // the table's own built-in toolbar and pagination would double-filter
              // and stack a second, disconnected paginator on top of it.
              <DataTable
                columns={columns}
                data={diagrams}
                showToolbar={false}
                showPagination={false}
              />
            )}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                disabled={diagrams.length < PAGE_SIZE}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}
